// Utility functions for report processing and calculations

import { ReportData } from "../../types/global.d";
import { sortReportsByDefaultPriority } from "../../utils/reportSorting";

export type FilterType = "all" | "not-cleaned" | "cleaned";

/**
 * Filter reports by search term
 */
export const filterReportsBySearch = (
  reports: ReportData[],
  searchTerm: string
): ReportData[] => {
  const trimmedSearch = searchTerm.trim();
  if (!trimmedSearch) return reports;

  const searchLower = trimmedSearch.toLowerCase();
  const numericSearchSequence = trimmedSearch.replace(/[^\d]/g, "");

  const normalizeCoordinateDigits = (value?: number): string => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "";
    }

    return value.toFixed(6).replace(/[^\d]/g, "");
  };

  return reports.filter((report) => {
    const matchesText =
      report.comment?.toLowerCase().includes(searchLower) ||
      report.user?.nickname?.toLowerCase().includes(searchLower) ||
      report.cleaned?.user?.nickname?.toLowerCase().includes(searchLower) ||
      report.locationName?.toLowerCase().includes(searchLower);

    if (matchesText) {
      return true;
    }

    if (!numericSearchSequence) {
      return false;
    }

    const latitudeDigits = normalizeCoordinateDigits(
      report.geoposition?.latitude
    );
    const longitudeDigits = normalizeCoordinateDigits(
      report.geoposition?.longitude
    );

    if (!latitudeDigits && !longitudeDigits) {
      return false;
    }

    const combinedDigits = `${latitudeDigits}${longitudeDigits}`;

    return (
      (latitudeDigits && latitudeDigits.includes(numericSearchSequence)) ||
      (longitudeDigits && longitudeDigits.includes(numericSearchSequence)) ||
      (combinedDigits && combinedDigits.includes(numericSearchSequence))
    );
  });
};

// Numeric statuses that carry a cleaning (report + cleaning combinations).
// Lets the cleaned/not-cleaned filter work on minimal-tier pins, which arrive
// with only `status` and no `cleaned` object until their preview loads.
const STATUSES_WITH_CLEANING = new Set<number>([4, 5, 7, 8]);

export const reportHasCleaning = (report: ReportData): boolean => {
  if (report.cleaned) {
    return true;
  }
  const numericStatus = Number(report.status);
  return Number.isFinite(numericStatus) && STATUSES_WITH_CLEANING.has(numericStatus);
};

/**
 * Filter reports by cleaning status
 */
export const filterReportsByStatus = (
  reports: ReportData[],
  filter: FilterType
): ReportData[] => {
  switch (filter) {
    case "cleaned":
      return reports.filter((report) => reportHasCleaning(report));
    case "not-cleaned":
      return reports.filter((report) => !reportHasCleaning(report));
    case "all":
    default:
      return reports;
  }
};

/**
 * Sort reports by moderation priority for admins, then freshness (newest first)
 */
export const sortReportsByPriority = (
  reports: ReportData[],
  isAdmin = false
): ReportData[] => {
  return sortReportsByDefaultPriority(reports, isAdmin);
};
